import React, {useState} from 'react';
import {
  View,
  Text,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Linking,
  Platform,
  ScrollView,
} from 'react-native';
import {AirbnbRating} from 'react-native-ratings';
import {useNavigation} from '@react-navigation/native';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import NPSService from '../../services/NPSService/NPSService';
import {useAuthStore} from '../../store/AuthStore';
import {AppRoutes} from '../../navigation/Routes.types';
import {Colors} from '../../constants/Colors';

interface NPSProps {
  onClose?: () => void;
}

const NPS: React.FC<NPSProps> = ({onClose = () => {}}) => {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const {current_client, clientCode} = useAuthStore().user;
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const getRatingColor = (rating: number | null) => {
    if (rating === null) return '#ccc';
    if (rating <= 6) return '#FF0000'; // Vermelho para 0-6
    if (rating <= 8) return '#FFD700'; // Amarelo para 7-8
    return '#00FF00'; // Verde para 9-10
  };

  const handleRatingCompleted = (score: number) => {
    setScore(score);
  };

  const saveNPS = async () => {
    const client = current_client?.clientCode || clientCode;
    const response = await NPSService.NPSPost(
      {score, comment, clientCode: client},
      client,
    );
    return response;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await saveNPS();
      setFeedbackSubmitted(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedirect = async () => {
    const iosAppId = '6736531681';
    const androidPackageName = 'com.conecta.brasal';

    const iosUrl = `itms-apps://itunes.apple.com/app/id${iosAppId}`;
    const androidUrl = `market://details?id=${androidPackageName}`;
    const iosWebUrl = `https://apps.apple.com/app/id${iosAppId}`;
    const androidWebUrl = `https://play.google.com/store/apps/details?id=${androidPackageName}`;

    const url = Platform.select({ios: iosUrl, android: androidUrl});
    const webUrl = Platform.select({ios: iosWebUrl, android: androidWebUrl});
    try {
      await Linking.openURL(url!);
    } catch (error) {
      await Linking.openURL(webUrl!);
    }
  };

  const navigation = useNavigation();

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}>
      <View style={styles.container}>
          {feedbackSubmitted ? (
            <>
              {score !== null && score >= 4 ? (
                <>
                  <Text style={styles.title}>
                    Obrigado pelo seu feedback! A sua opinião é muito importante para nós.
                  </Text>
                  <Text style={styles.title}>
                    Gostaria de avaliar o Conecta na loja de aplicativos?
                  </Text>
                  <View style={{gap: 10}}>
                    <ButtonComponent title="Avaliar" onPress={handleRedirect} />
                    <ButtonComponent
                      title="Voltar"
                      outlined
                      onPress={onClose}
                    />
                  </View>
                </>
              ) : (
                <>
                  <Text style={styles.title}>
                    Obrigado pelo seu feedback! A sua opinião é muito importante para nós.
                  </Text>
                  <View style={{gap: 10}}>
                    <ButtonComponent
                      title="Voltar"
                      outlined
                      onPress={onClose}
                    />
                  </View>
                </>
              )}
            </>
          ) : (
            <>
              <Text style={styles.headerTitle}>Avalie sua compra</Text>
              <Text style={styles.subtitle}>
                O que você achou da sua experiência de compra?
              </Text>
              <Text style={[styles.scoreText, {color: getRatingColor(score)}]}>
                {score !== null ? `${score}/10` : ''}
              </Text>

              {/* AirbnbRating com key dinâmica */}
              <AirbnbRating
                key={score ?? 'initial'}
                count={10}
                reviews={['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
                defaultRating={score ?? 0}
                size={25}
                onFinishRating={handleRatingCompleted}
                selectedColor={getRatingColor(score)}
                showRating={false}
              />

              <TextInput
                style={styles.textInput}
                placeholder="Deixe um comentário"
                placeholderTextColor="#999"
                multiline
                numberOfLines={4}
                onChangeText={text => setComment(text)}
                value={comment}
              />

              {score === null && (
                <Text style={styles.warningText}>
                  Escolha uma nota antes de enviar!
                </Text>
              )}

              {loading ? (
                <ActivityIndicator size="large" color={Colors.RedPallete.red} />
              ) : (
                <ButtonComponent
                  title="Enviar"
                  onPress={handleSubmit}
                  disabled={score === null}
                />
              )}
            </>
          )}
        </View>
      </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 16,
  },
  container: {
    padding: 20,
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    marginBottom: 10,
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#333',
  },
  headerTitle: {
    fontSize: 30,
    lineHeight: 26,
    color: 'black',
    textAlign: 'center',
    maxWidth: 300,
    alignSelf: 'center',
    fontFamily: 'TCCC-UnityText Bold',
    marginTop: 20,
  },
  subtitle: {
    fontSize: 16,
    lineHeight: 18,
    color: '#818181',
    textAlign: 'left',
    maxWidth: 300,
    alignSelf: 'center',
    fontFamily: 'TCCC-UnityText Bold',
    marginTop: 30,
  },
  scoreText: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
  },
  textInput: {
    height: 100,
    borderColor: 'gray',
    borderWidth: 1,
    marginTop: 15,
    padding: 10,
    borderRadius: 5,
    textAlignVertical: 'top',
    marginBottom: 20,
    color: '#333',
  },
  warningText: {
    fontSize: 16,
    lineHeight: 18,
    color: '#818181',
    textAlign: 'left',
    alignSelf: 'center',
    fontFamily: 'TCCC-UnityText Bold',
    marginBottom: 15,
  },
});

export default NPS;
